export const renderRichText = (text: string, facets?: any[], options?: { onlyFacets?: boolean }) => {
    if (!text) return '';

    const onlyFacets = options?.onlyFacets ?? false;

    if (facets === undefined) {
        // Fallback for profiles/bios without facets (undefined)
        // We use full naive rendering as these usually don't have facets from the API
        return naiveRender(escapeString(text));
    }

    if (facets.length === 0) {
        if (onlyFacets) return escapeString(text);
        // For posts where facets are an empty array, it means rich text was processed
        // but no valid facets were found. We should only auto-linkify URLs.
        return naiveRender(escapeString(text), { linkifyURLs: true, linkifyMentions: false, linkifyHashtags: false });
    }

    // Facet-aware rendering for posts
    const sortedFacets = [...facets].sort((a, b) => a.index.byteStart - b.index.byteStart);

    const textEncoder = new TextEncoder();
    const textDecoder = new TextDecoder();
    const bytes = textEncoder.encode(text);

    let result = '';
    let lastByteIndex = 0;

    // For posts WITH facets, we only auto-linkify URLs in the non-faceted parts.
    // Mentions and hashtags must be explicitly defined by facets to be clickable.
    // When onlyFacets is true, no auto-linkification happens in gap text.
    const gapOptions = onlyFacets
        ? { linkifyURLs: false, linkifyMentions: false, linkifyHashtags: false }
        : { linkifyURLs: true, linkifyMentions: false, linkifyHashtags: false };

    for (const facet of sortedFacets) {
        const { byteStart, byteEnd } = facet.index;

        // Add text before the facet, run naiveRender on it
        result += naiveRender(escapeString(textDecoder.decode(bytes.slice(lastByteIndex, byteStart))), gapOptions);

        // Get the faceted text
        const facetedText = textDecoder.decode(bytes.slice(byteStart, byteEnd));
        const escapedFacetedText = escapeString(facetedText);

        let facetHtml = escapedFacetedText;

        for (const feature of facet.features) {
            if (feature.$type === 'app.bsky.richtext.facet#link') {
                facetHtml = `<a href="${feature.uri}" target="_blank" class="text-primary text-decoration-underline" style="pointer-events: auto;">${escapedFacetedText}</a>`;
            } else if (feature.$type === 'app.bsky.richtext.facet#mention') {
                // Feature did for mentions is the DID
                facetHtml = `<a href="#" class="mention" data-handle="${feature.did}" data-did="true" style="pointer-events: auto;">${escapedFacetedText}</a>`;
            } else if (feature.$type === 'app.bsky.richtext.facet#tag') {
                facetHtml = `<a href="#" class="hashtag text-primary" style="pointer-events: auto;">${escapedFacetedText}</a>`;
            }
        }

        result += facetHtml;
        lastByteIndex = byteEnd;
    }

    // Add remaining text, run naiveRender on it
    result += naiveRender(escapeString(textDecoder.decode(bytes.slice(lastByteIndex))), gapOptions);

    return result;
};

/**
 * Escapes HTML special characters.
 * Apostrophes and quotes are omitted because they don't strictly need escaping in text nodes
 * and were causing literal entity display issues in some environments.
 * We still escape &, <, and > for safe v-html usage.
 */
const escapeString = (str: string) => {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

interface NaiveRenderOptions {
    linkifyURLs?: boolean;
    linkifyMentions?: boolean;
    linkifyHashtags?: boolean;
}

const naiveRender = (escaped: string, options: NaiveRenderOptions = { linkifyURLs: true, linkifyMentions: true, linkifyHashtags: true }) => {
    // Single regex to match all potential tokens: URLs, Mentions (@), and Hashtags (#)
    // URL pattern excludes emails by ensuring there is no @ immediately before the domain-like part if it's not a protocol
    // Group 1: URL
    // Group 2: Mention (preceded by start of line or whitespace, REQUIRES at least one DOT for valid handle)
    // Group 3: Hashtag (preceded by start of line or whitespace)
    const patterns: string[] = [];
    if (options.linkifyURLs) patterns.push('((?:https?://|www\\.)[^\\s\\n\\r\\t]+|(?:\\b[a-z0-9-]+(?:\\.[a-z0-9-]+)*\\.[a-z]{2,}(?:\\/[^\\s\\n\\r\\t]*)?\\b))');
    if (options.linkifyMentions) patterns.push('((?:^|\\s)@[a-zA-Z0-9-]+\\.[a-zA-Z0-9.-]+[a-zA-Z0-9])');
    if (options.linkifyHashtags) patterns.push('((?:^|\\s)#\\w+)');

    if (patterns.length === 0) return escaped;

    const combinedPattern = new RegExp(patterns.join('|'), 'gi');

    return escaped.replace(combinedPattern, (match, ...args) => {
        // args contains captured groups, then offset, then original string
        // Since groups depend on options, we need to find which one matched
        let url, mention, hashtag;
        let groupIdx = 0;
        if (options.linkifyURLs) url = args[groupIdx++];
        if (options.linkifyMentions) mention = args[groupIdx++];
        if (options.linkifyHashtags) hashtag = args[groupIdx++];
        
        const offset = args[args.length - 2];
        const string = args[args.length - 1];

        if (url) {
            // Check if this looks like an email:
            // 1. It contains @ and no protocol
            // 2. OR it is preceded by @ (domain part of an email)
            const isEmail = (url.includes('@') && !url.toLowerCase().startsWith('http')) || 
                           (offset > 0 && string[offset - 1] === '@');
            
            if (isEmail) {
                return url; // Don't linkify emails
            }

            let clean = url;
            let trailing = '';
            const punctuation = /[.,;:!?\"')\]]+$/;
            const pMatch = clean.match(punctuation);
            if (pMatch) {
                clean = clean.substring(0, clean.length - pMatch[0].length);
                trailing = pMatch[0];
            }

            const tldMatch = clean.match(/\.([a-z]{2,})$/i);
            if (!tldMatch && !clean.includes('/')) return match;

            let href = clean;
            if (!href.toLowerCase().startsWith('http')) {
                href = 'https://' + clean;
            }

            return `<a href="${href}" target="_blank" class="text-primary text-decoration-underline" style="pointer-events: auto;">${clean}</a>${trailing}`;
        }

        if (mention) {
            // mention includes the potential leading space
            const space = mention.match(/^\s/) ? mention[0] : '';
            const handle = mention.trim().substring(1);
            return `${space}<a href="#" class="mention" data-handle="${handle}" style="pointer-events: auto;">@${handle}</a>`;
        }

        if (hashtag) {
            // hashtag includes the potential leading space
            const space = hashtag.match(/^\s/) ? hashtag[0] : '';
            const tag = hashtag.trim();
            return `${space}<a href="#" class="hashtag text-primary" style="pointer-events: auto;">${tag}</a>`;
        }

        return match;
    });
}

export const handleRichTextClick = (event: MouseEvent, { router, onTagClick }: { router: any, onTagClick?: (tag: string) => void }) => {
    const target = event.target as HTMLElement;
    const hashSpan = target.closest('.hashtag');
    const mentionLink = target.closest('.mention');
    const anchorLink = target.closest('a');

    if (hashSpan) {
        event.preventDefault();
        event.stopPropagation();
        const tag = hashSpan.textContent?.replace('#', '');
        if (tag) {
            if (onTagClick) {
                onTagClick(tag);
            } else {
                window.open(`https://bsky.app/search?q=%23${tag}`, '_blank');
            }
        }
    } else if (mentionLink) {
        event.preventDefault();
        event.stopPropagation();
        const handle = (mentionLink as HTMLElement).dataset.handle;
        if (handle) {
            router.push(`/profile/${handle}`);
        }
    } else if (anchorLink) {
        event.stopPropagation();
    }
};
